import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DrawIcon from "@mui/icons-material/Draw";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * SignatureCapture Component
 * A simple inline signature pad for registration forms
 *
 * @param {function} onSignatureChange - Callback when signature changes (base64 string)
 * @param {string} existingSignature - Existing signature to display (base64)
 * @param {string} label - Label text
 * @param {boolean} error - Show error state
 * @param {string} helperText - Helper/error text
 * @param {boolean} disabled - Disable the signature pad
 * @param {string} primaryColor - Theme primary color
 */
const SignatureCapture = ({
  onSignatureChange,
  existingSignature = null,
  label = "Your Signature",
  error = false,
  helperText = "",
  disabled = false,
  primaryColor = "#11998e",
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // Load existing signature if available
      if (existingSignature) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          setHasSignature(true);
        };
        img.src = existingSignature;
      }
    }
  }, [existingSignature]);

  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPosition(e);

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPosition(e);

    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setHasSignature(true);
      // Save signature as base64
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      onSignatureChange(signatureData);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange("");
  };

  // If showing existing signature in view-only mode
  if (disabled && existingSignature) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          textAlign: "center",
          bgcolor: "#f5fff5",
          borderColor: "#4caf50",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
          <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
          <Typography variant="subtitle2" color="success.main">
            Signature Captured
          </Typography>
        </Box>
        <Box
          component="img"
          src={existingSignature}
          alt="Signature"
          sx={{
            maxWidth: "100%",
            maxHeight: 100,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            bgcolor: "white",
          }}
        />
      </Paper>
    );
  }

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: error ? "#d32f2f" : "#333",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <DrawIcon sx={{ fontSize: 20, color: error ? "#d32f2f" : primaryColor }} />
        {label} *
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          borderColor: error ? "#d32f2f" : hasSignature ? primaryColor : "#ccc",
          borderWidth: 2,
          borderStyle: "dashed",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: error ? "rgba(211, 47, 47, 0.04)" : hasSignature ? `${primaryColor}08` : "#fafafa",
          transition: "all 0.3s ease",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <canvas
            ref={canvasRef}
            width={500}
            height={150}
            style={{
              width: "100%",
              height: "150px",
              cursor: disabled ? "not-allowed" : "crosshair",
              touchAction: "none",
              backgroundColor: "white",
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />

          {/* Placeholder text */}
          {!hasSignature && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
                opacity: 0.5,
                textAlign: "center",
              }}
            >
              Draw your signature here
              <br />
              <Typography variant="caption" component="span">
                (Use mouse or finger)
              </Typography>
            </Typography>
          )}
        </Box>

        {/* Clear Button */}
        {hasSignature && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, bgcolor: "#f5f5f5" }}>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={clearSignature}
              sx={{ color: "#666", textTransform: "none" }}
            >
              Clear Signature
            </Button>
          </Box>
        )}
      </Paper>

      {/* Helper/Error Text */}
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? "#d32f2f" : "#666",
            mt: 0.5,
            display: "block",
          }}
        >
          {helperText}
        </Typography>
      )}

      {/* Legal Notice */}
      <Typography
        variant="caption"
        sx={{
          color: "#999",
          mt: 1,
          display: "block",
          fontStyle: "italic",
        }}
      >
        By signing, you agree that this electronic signature is legally binding.
      </Typography>
    </Box>
  );
};

export default SignatureCapture;
